import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./SettingsModal.scss";
import type { MapTileLayer } from "../utils/types";
import {
  DEFAULT_NAME_BY_KEY,
  type ChartConfig,
  type ChartKey,
  type MapActualBitrateColoring,
} from "../utils/settings";

type Props = {
  isOpen: boolean;
  initialDelayOffsetMs: number;
  initialCharts: ChartConfig[];
  initialFollowMap: boolean;
  initialShowVideo: boolean;
  initialDisableChartDownsample: boolean;
  initialDisableMapDownsample: boolean;
  initialMapTileLayer: MapTileLayer;
  initialMapActualBitrateColoring: MapActualBitrateColoring;
  showDownsampleSettings?: boolean;
  showHeatmapCellMeters?: boolean;
  initialHeatmapCellMeters?: number;
  initialHeatmapBaseOpacity?: number;
  initialHeatmapRenderMode?: "grid" | "smooth";
  initialHeatmapGridScoreColorMid?: number;
  initialHeatmapGridScoreColorLow?: number;
  initialHeatmapSmoothScoreColorMid?: number;
  initialHeatmapSmoothScoreColorLow?: number;
  defaultDelayOffsetMs: number;
  defaultCharts: ChartConfig[];
  defaultFollowMap: boolean;
  defaultShowVideo: boolean;
  defaultDisableChartDownsample: boolean;
  defaultDisableMapDownsample: boolean;
  defaultMapTileLayer: MapTileLayer;
  defaultMapActualBitrateColoring: MapActualBitrateColoring;
  defaultHeatmapCellMeters?: number;
  defaultHeatmapBaseOpacity?: number;
  defaultHeatmapRenderMode?: "grid" | "smooth";
  defaultHeatmapGridScoreColorMid?: number;
  defaultHeatmapGridScoreColorLow?: number;
  defaultHeatmapSmoothScoreColorMid?: number;
  defaultHeatmapSmoothScoreColorLow?: number;
  onApply: (settings: {
    delayOffsetMs: number;
    charts: ChartConfig[];
    followMap: boolean;
    showVideo: boolean;
    disableUiCap: boolean;
    disableChartDownsample: boolean;
    disableMapDownsample: boolean;
    mapTileLayer: MapTileLayer;
    mapActualBitrateColoring: MapActualBitrateColoring;
    heatmapCellMeters?: number;
    heatmapBaseOpacity?: number;
    heatmapRenderMode?: "grid" | "smooth";
    heatmapGridScoreColorMid?: number;
    heatmapGridScoreColorLow?: number;
    heatmapSmoothScoreColorMid?: number;
    heatmapSmoothScoreColorLow?: number;
  }) => void;
  onClose: () => void;
};

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const SettingsModal = ({
  isOpen,
  initialDelayOffsetMs,
  initialCharts,
  initialFollowMap,
  initialShowVideo,
  initialDisableChartDownsample,
  initialDisableMapDownsample,
  initialMapTileLayer,
  initialMapActualBitrateColoring,
  showDownsampleSettings = true,
  showHeatmapCellMeters,
  initialHeatmapCellMeters,
  initialHeatmapBaseOpacity,
  initialHeatmapRenderMode,
  initialHeatmapGridScoreColorMid,
  initialHeatmapGridScoreColorLow,
  initialHeatmapSmoothScoreColorMid,
  initialHeatmapSmoothScoreColorLow,
  defaultDelayOffsetMs,
  defaultCharts,
  defaultFollowMap,
  defaultShowVideo,
  defaultDisableChartDownsample,
  defaultDisableMapDownsample,
  defaultMapTileLayer,
  defaultMapActualBitrateColoring,
  defaultHeatmapCellMeters,
  defaultHeatmapBaseOpacity,
  defaultHeatmapRenderMode,
  defaultHeatmapGridScoreColorMid,
  defaultHeatmapGridScoreColorLow,
  defaultHeatmapSmoothScoreColorMid,
  defaultHeatmapSmoothScoreColorLow,
  onApply,
  onClose,
}: Props) => {
  const [delayOffsetInput, setDelayOffsetInput] = useState<string>(
    String(initialDelayOffsetMs),
  );

  const [chartsDraft, setChartsDraft] = useState<ChartConfig[]>(() =>
    (initialCharts ?? []).map((c) => ({ ...c })),
  );

  const [followMapDraft, setFollowMapDraft] =
    useState<boolean>(initialFollowMap);

  const [showVideoDraft, setShowVideoDraft] =
    useState<boolean>(initialShowVideo);

  const [disableChartDownsampleDraft, setDisableChartDownsampleDraft] =
    useState<boolean>(initialDisableChartDownsample);

  const [disableMapDownsampleDraft, setDisableMapDownsampleDraft] =
    useState<boolean>(initialDisableMapDownsample);

  const [mapTileLayerDraft, setMapTileLayerDraft] =
    useState<MapTileLayer>(initialMapTileLayer);

  const [heatmapCellMetersInput, setHeatmapCellMetersInput] = useState<string>(
    String(
      typeof initialHeatmapCellMeters === "number" &&
        Number.isFinite(initialHeatmapCellMeters)
        ? Math.trunc(initialHeatmapCellMeters)
        : typeof defaultHeatmapCellMeters === "number" &&
            Number.isFinite(defaultHeatmapCellMeters)
          ? Math.trunc(defaultHeatmapCellMeters)
          : 30,
    ),
  );

  const [heatmapBaseOpacityPctInput, setHeatmapBaseOpacityPctInput] =
    useState<string>(
      String(
        Math.round(
          100 *
            (typeof initialHeatmapBaseOpacity === "number" &&
            Number.isFinite(initialHeatmapBaseOpacity)
              ? clamp(initialHeatmapBaseOpacity, 0, 1)
              : typeof defaultHeatmapBaseOpacity === "number" &&
                  Number.isFinite(defaultHeatmapBaseOpacity)
                ? clamp(defaultHeatmapBaseOpacity, 0, 1)
                : 0.08),
        ),
      ),
    );

  const [heatmapRenderModeDraft, setHeatmapRenderModeDraft] = useState<
    "grid" | "smooth"
  >(
    initialHeatmapRenderMode === "grid" || initialHeatmapRenderMode === "smooth"
      ? initialHeatmapRenderMode
      : defaultHeatmapRenderMode === "grid" ||
          defaultHeatmapRenderMode === "smooth"
        ? defaultHeatmapRenderMode
        : "smooth",
  );

  const [heatmapGridScoreColorMidInput, setHeatmapGridScoreColorMidInput] =
    useState<string>(
      String(
        typeof initialHeatmapGridScoreColorMid === "number" &&
          Number.isFinite(initialHeatmapGridScoreColorMid)
          ? clamp(initialHeatmapGridScoreColorMid, 0, 1)
          : typeof initialHeatmapSmoothScoreColorMid === "number" &&
              Number.isFinite(initialHeatmapSmoothScoreColorMid)
            ? clamp(initialHeatmapSmoothScoreColorMid, 0, 1)
            : typeof defaultHeatmapGridScoreColorMid === "number" &&
                Number.isFinite(defaultHeatmapGridScoreColorMid)
              ? clamp(defaultHeatmapGridScoreColorMid, 0, 1)
              : typeof defaultHeatmapSmoothScoreColorMid === "number" &&
                  Number.isFinite(defaultHeatmapSmoothScoreColorMid)
                ? clamp(defaultHeatmapSmoothScoreColorMid, 0, 1)
                : 0.5,
      ),
    );

  const [heatmapGridScoreColorLowInput, setHeatmapGridScoreColorLowInput] =
    useState<string>(
      String(
        typeof initialHeatmapGridScoreColorLow === "number" &&
          Number.isFinite(initialHeatmapGridScoreColorLow)
          ? clamp(initialHeatmapGridScoreColorLow, 0, 1)
          : typeof initialHeatmapSmoothScoreColorLow === "number" &&
              Number.isFinite(initialHeatmapSmoothScoreColorLow)
            ? clamp(initialHeatmapSmoothScoreColorLow, 0, 1)
            : typeof defaultHeatmapGridScoreColorLow === "number" &&
                Number.isFinite(defaultHeatmapGridScoreColorLow)
              ? clamp(defaultHeatmapGridScoreColorLow, 0, 1)
              : typeof defaultHeatmapSmoothScoreColorLow === "number" &&
                  Number.isFinite(defaultHeatmapSmoothScoreColorLow)
                ? clamp(defaultHeatmapSmoothScoreColorLow, 0, 1)
                : 0.9,
      ),
    );

  const [thresholdLowKbpsInput, setThresholdLowKbpsInput] = useState<string>(
    String(initialMapActualBitrateColoring.thresholdLowKbps),
  );
  const [thresholdMidKbpsInput, setThresholdMidKbpsInput] = useState<string>(
    String(initialMapActualBitrateColoring.thresholdMidKbps),
  );
  const [lowColorDraft, setLowColorDraft] = useState<string>(
    initialMapActualBitrateColoring.lowColor,
  );
  const [midColorDraft, setMidColorDraft] = useState<string>(
    initialMapActualBitrateColoring.midColor,
  );
  const [highColorDraft, setHighColorDraft] = useState<string>(
    initialMapActualBitrateColoring.highColor,
  );

  useEffect(() => {
    if (!isOpen) return;
    setDelayOffsetInput(String(initialDelayOffsetMs));
    setChartsDraft((initialCharts ?? []).map((c) => ({ ...c })));
    setFollowMapDraft(initialFollowMap);
    setShowVideoDraft(initialShowVideo);
    setDisableChartDownsampleDraft(initialDisableChartDownsample);
    setDisableMapDownsampleDraft(initialDisableMapDownsample);
    setMapTileLayerDraft(initialMapTileLayer);
    setHeatmapCellMetersInput(
      String(
        typeof initialHeatmapCellMeters === "number" &&
          Number.isFinite(initialHeatmapCellMeters)
          ? Math.trunc(initialHeatmapCellMeters)
          : typeof defaultHeatmapCellMeters === "number" &&
              Number.isFinite(defaultHeatmapCellMeters)
            ? Math.trunc(defaultHeatmapCellMeters)
            : 30,
      ),
    );
    setHeatmapBaseOpacityPctInput(
      String(
        Math.round(
          100 *
            (typeof initialHeatmapBaseOpacity === "number" &&
            Number.isFinite(initialHeatmapBaseOpacity)
              ? clamp(initialHeatmapBaseOpacity, 0, 1)
              : typeof defaultHeatmapBaseOpacity === "number" &&
                  Number.isFinite(defaultHeatmapBaseOpacity)
                ? clamp(defaultHeatmapBaseOpacity, 0, 1)
                : 0.08),
        ),
      ),
    );
    setHeatmapRenderModeDraft(
      initialHeatmapRenderMode === "grid" ||
        initialHeatmapRenderMode === "smooth"
        ? initialHeatmapRenderMode
        : defaultHeatmapRenderMode === "grid" ||
            defaultHeatmapRenderMode === "smooth"
          ? defaultHeatmapRenderMode
          : "smooth",
    );
    setHeatmapGridScoreColorMidInput(
      String(
        typeof initialHeatmapGridScoreColorMid === "number" &&
          Number.isFinite(initialHeatmapGridScoreColorMid)
          ? clamp(initialHeatmapGridScoreColorMid, 0, 1)
          : typeof initialHeatmapSmoothScoreColorMid === "number" &&
              Number.isFinite(initialHeatmapSmoothScoreColorMid)
            ? clamp(initialHeatmapSmoothScoreColorMid, 0, 1)
            : typeof defaultHeatmapGridScoreColorMid === "number" &&
                Number.isFinite(defaultHeatmapGridScoreColorMid)
              ? clamp(defaultHeatmapGridScoreColorMid, 0, 1)
              : typeof defaultHeatmapSmoothScoreColorMid === "number" &&
                  Number.isFinite(defaultHeatmapSmoothScoreColorMid)
                ? clamp(defaultHeatmapSmoothScoreColorMid, 0, 1)
                : 0.5,
      ),
    );
    setHeatmapGridScoreColorLowInput(
      String(
        typeof initialHeatmapGridScoreColorLow === "number" &&
          Number.isFinite(initialHeatmapGridScoreColorLow)
          ? clamp(initialHeatmapGridScoreColorLow, 0, 1)
          : typeof initialHeatmapSmoothScoreColorLow === "number" &&
              Number.isFinite(initialHeatmapSmoothScoreColorLow)
            ? clamp(initialHeatmapSmoothScoreColorLow, 0, 1)
            : typeof defaultHeatmapGridScoreColorLow === "number" &&
                Number.isFinite(defaultHeatmapGridScoreColorLow)
              ? clamp(defaultHeatmapGridScoreColorLow, 0, 1)
              : typeof defaultHeatmapSmoothScoreColorLow === "number" &&
                  Number.isFinite(defaultHeatmapSmoothScoreColorLow)
                ? clamp(defaultHeatmapSmoothScoreColorLow, 0, 1)
                : 0.9,
      ),
    );
    setThresholdLowKbpsInput(
      String(initialMapActualBitrateColoring.thresholdLowKbps),
    );
    setThresholdMidKbpsInput(
      String(initialMapActualBitrateColoring.thresholdMidKbps),
    );
    setLowColorDraft(initialMapActualBitrateColoring.lowColor);
    setMidColorDraft(initialMapActualBitrateColoring.midColor);
    setHighColorDraft(initialMapActualBitrateColoring.highColor);
  }, [
    isOpen,
    initialDelayOffsetMs,
    initialCharts,
    initialFollowMap,
    initialShowVideo,
    initialDisableChartDownsample,
    initialDisableMapDownsample,
    initialMapTileLayer,
    initialMapActualBitrateColoring,
    initialHeatmapCellMeters,
    initialHeatmapBaseOpacity,
    initialHeatmapRenderMode,
    initialHeatmapGridScoreColorMid,
    initialHeatmapGridScoreColorLow,
    initialHeatmapSmoothScoreColorMid,
    initialHeatmapSmoothScoreColorLow,
    defaultHeatmapGridScoreColorMid,
    defaultHeatmapGridScoreColorLow,
    defaultHeatmapSmoothScoreColorMid,
    defaultHeatmapSmoothScoreColorLow,
    defaultHeatmapCellMeters,
    defaultHeatmapBaseOpacity,
    defaultHeatmapRenderMode,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const parsedDelayOffset = useMemo(() => {
    const n = Number(delayOffsetInput);
    if (!Number.isFinite(n)) return null;
    return clamp(Math.trunc(n), -60_000, 60_000);
  }, [delayOffsetInput]);

  const canApply = parsedDelayOffset !== null;

  const handleReset = () => {
    setDelayOffsetInput(String(defaultDelayOffsetMs));
    setChartsDraft((defaultCharts ?? []).map((c) => ({ ...c })));
    setFollowMapDraft(defaultFollowMap);
    setShowVideoDraft(defaultShowVideo);
    if (showDownsampleSettings) {
      setDisableChartDownsampleDraft(defaultDisableChartDownsample);
      setDisableMapDownsampleDraft(defaultDisableMapDownsample);
    }
    setMapTileLayerDraft(defaultMapTileLayer);
    setHeatmapCellMetersInput(
      String(
        typeof defaultHeatmapCellMeters === "number" &&
          Number.isFinite(defaultHeatmapCellMeters)
          ? Math.trunc(defaultHeatmapCellMeters)
          : 30,
      ),
    );
    setHeatmapBaseOpacityPctInput(
      String(
        Math.round(
          100 *
            (typeof defaultHeatmapBaseOpacity === "number" &&
            Number.isFinite(defaultHeatmapBaseOpacity)
              ? clamp(defaultHeatmapBaseOpacity, 0, 1)
              : 0.08),
        ),
      ),
    );
    setHeatmapRenderModeDraft(
      defaultHeatmapRenderMode === "grid" ||
        defaultHeatmapRenderMode === "smooth"
        ? defaultHeatmapRenderMode
        : "smooth",
    );
    setHeatmapGridScoreColorMidInput(
      String(
        typeof defaultHeatmapGridScoreColorMid === "number" &&
          Number.isFinite(defaultHeatmapGridScoreColorMid)
          ? clamp(defaultHeatmapGridScoreColorMid, 0, 1)
          : typeof defaultHeatmapSmoothScoreColorMid === "number" &&
              Number.isFinite(defaultHeatmapSmoothScoreColorMid)
            ? clamp(defaultHeatmapSmoothScoreColorMid, 0, 1)
            : 0.5,
      ),
    );
    setHeatmapGridScoreColorLowInput(
      String(
        typeof defaultHeatmapGridScoreColorLow === "number" &&
          Number.isFinite(defaultHeatmapGridScoreColorLow)
          ? clamp(defaultHeatmapGridScoreColorLow, 0, 1)
          : typeof defaultHeatmapSmoothScoreColorLow === "number" &&
              Number.isFinite(defaultHeatmapSmoothScoreColorLow)
            ? clamp(defaultHeatmapSmoothScoreColorLow, 0, 1)
            : 0.9,
      ),
    );
    setThresholdLowKbpsInput(
      String(defaultMapActualBitrateColoring.thresholdLowKbps),
    );
    setThresholdMidKbpsInput(
      String(defaultMapActualBitrateColoring.thresholdMidKbps),
    );
    setLowColorDraft(defaultMapActualBitrateColoring.lowColor);
    setMidColorDraft(defaultMapActualBitrateColoring.midColor);
    setHighColorDraft(defaultMapActualBitrateColoring.highColor);
  };

  const updateChart = (idx: number, patch: Partial<ChartConfig>) => {
    setChartsDraft((prev) => {
      if (idx < 0 || idx >= prev.length) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const handleChangeSeries = (idx: number, nextKey: ChartKey) => {
    setChartsDraft((prev) => {
      if (idx < 0 || idx >= prev.length) return prev;
      const cur = prev[idx];
      const next = [...prev];
      const curName = typeof cur?.name === "string" ? cur.name : "";
      const curDefault = DEFAULT_NAME_BY_KEY[cur.key];
      const nextDefault = DEFAULT_NAME_BY_KEY[nextKey];
      const shouldAutoRename = curName.trim() === "" || curName === curDefault;
      next[idx] = {
        ...cur,
        key: nextKey,
        name: shouldAutoRename ? nextDefault : curName,
      };
      return next;
    });
  };

  const handleApply = () => {
    if (parsedDelayOffset === null) return;

    const disableUiCap =
      Boolean(disableChartDownsampleDraft) ||
      Boolean(disableMapDownsampleDraft);

    const parsedCellMeters = Math.trunc(Number(heatmapCellMetersInput));
    const safeCellMetersRaw =
      Number.isFinite(parsedCellMeters) && parsedCellMeters > 0
        ? parsedCellMeters
        : typeof defaultHeatmapCellMeters === "number" &&
            Number.isFinite(defaultHeatmapCellMeters)
          ? Math.trunc(defaultHeatmapCellMeters)
          : 30;
    const safeCellMeters = clamp(safeCellMetersRaw, 5, 500);

    const parsedOpacityPct = Number(heatmapBaseOpacityPctInput);
    const safeOpacityPctRaw =
      Number.isFinite(parsedOpacityPct) && parsedOpacityPct >= 0
        ? parsedOpacityPct
        : Math.round(
            100 *
              (typeof defaultHeatmapBaseOpacity === "number" &&
              Number.isFinite(defaultHeatmapBaseOpacity)
                ? clamp(defaultHeatmapBaseOpacity, 0, 1)
                : 0.08),
          );
    const safeOpacity01 = clamp(safeOpacityPctRaw, 0, 100) / 100;

    const parsedScoreMid = Number(heatmapGridScoreColorMidInput);
    const parsedScoreLow = Number(heatmapGridScoreColorLowInput);

    const fallbackMid =
      typeof defaultHeatmapGridScoreColorMid === "number" &&
      Number.isFinite(defaultHeatmapGridScoreColorMid)
        ? defaultHeatmapGridScoreColorMid
        : typeof defaultHeatmapSmoothScoreColorMid === "number" &&
            Number.isFinite(defaultHeatmapSmoothScoreColorMid)
          ? defaultHeatmapSmoothScoreColorMid
          : 0.5;
    const fallbackLow =
      typeof defaultHeatmapGridScoreColorLow === "number" &&
      Number.isFinite(defaultHeatmapGridScoreColorLow)
        ? defaultHeatmapGridScoreColorLow
        : typeof defaultHeatmapSmoothScoreColorLow === "number" &&
            Number.isFinite(defaultHeatmapSmoothScoreColorLow)
          ? defaultHeatmapSmoothScoreColorLow
          : 0.9;

    const midRaw =
      Number.isFinite(parsedScoreMid) && parsedScoreMid >= 0
        ? parsedScoreMid
        : typeof defaultHeatmapGridScoreColorMid === "number" &&
            Number.isFinite(defaultHeatmapGridScoreColorMid)
          ? defaultHeatmapGridScoreColorMid
          : fallbackMid;
    const lowRaw =
      Number.isFinite(parsedScoreLow) && parsedScoreLow >= 0
        ? parsedScoreLow
        : typeof defaultHeatmapGridScoreColorLow === "number" &&
            Number.isFinite(defaultHeatmapGridScoreColorLow)
          ? defaultHeatmapGridScoreColorLow
          : fallbackLow;

    const safeScoreMid = clamp(midRaw, 0.01, 0.99);
    const safeScoreLow = Math.max(safeScoreMid, clamp(lowRaw, 0.01, 1));

    const parsedLow = Math.trunc(Number(thresholdLowKbpsInput));
    const parsedMid = Math.trunc(Number(thresholdMidKbpsInput));
    const safeLow =
      Number.isFinite(parsedLow) && parsedLow >= 0
        ? parsedLow
        : defaultMapActualBitrateColoring.thresholdLowKbps;
    const safeMidRaw =
      Number.isFinite(parsedMid) && parsedMid >= 0
        ? parsedMid
        : defaultMapActualBitrateColoring.thresholdMidKbps;
    const safeMid = Math.max(safeLow, safeMidRaw);

    onApply({
      delayOffsetMs: parsedDelayOffset,
      charts: chartsDraft,
      followMap: followMapDraft,
      showVideo: showVideoDraft,
      disableUiCap,
      disableChartDownsample: disableChartDownsampleDraft,
      disableMapDownsample: disableMapDownsampleDraft,
      mapTileLayer: mapTileLayerDraft,
      mapActualBitrateColoring: {
        thresholdLowKbps: safeLow,
        thresholdMidKbps: safeMid,
        lowColor: lowColorDraft,
        midColor: midColorDraft,
        highColor: highColorDraft,
      },
      ...(showHeatmapCellMeters
        ? {
            heatmapCellMeters: safeCellMeters,
            heatmapBaseOpacity: safeOpacity01,
            heatmapRenderMode: heatmapRenderModeDraft,
            heatmapGridScoreColorMid: safeScoreMid,
            heatmapGridScoreColorLow: safeScoreLow,
            heatmapSmoothScoreColorMid: safeScoreMid,
            heatmapSmoothScoreColorLow: safeScoreLow,
          }
        : {}),
    });
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="bpslogModalOverlay"
      role="dialog"
      aria-modal="true"
      aria-label="BpsLog Settings Modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bpslogModal bpslogSettingsModal">
        <div className="bpslogModalHeader">設定</div>

        <div className="bpslogModalBody">
          {showDownsampleSettings ? (
            <>
              <div className="bpslogSettingsSectionTitle">データ</div>
              <div className="bpslogSettingsSectionBody">
                <label className="bpslogModalField">
                  <span>取得数</span>
                  <div
                    className="bpslogSettingsRadioGroup"
                    role="radiogroup"
                    aria-label="Downsample mode"
                  >
                    {showHeatmapCellMeters ? (
                      <>
                        <label
                          className={`bpslogSettingsRadio ${
                            !disableMapDownsampleDraft ? "isActive" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="downsampleMode"
                            value="cap"
                            checked={!disableMapDownsampleDraft}
                            onChange={() => {
                              setDisableMapDownsampleDraft(false);
                            }}
                          />
                          間引く
                        </label>
                        <label
                          className={`bpslogSettingsRadio ${
                            disableMapDownsampleDraft ? "isActive" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="downsampleMode"
                            value="no_cap_map"
                            checked={disableMapDownsampleDraft}
                            onChange={() => {
                              setDisableMapDownsampleDraft(true);
                            }}
                          />
                          地図を間引かない
                        </label>
                      </>
                    ) : (
                      <>
                        <label
                          className={`bpslogSettingsRadio ${
                            !disableChartDownsampleDraft &&
                            !disableMapDownsampleDraft
                              ? "isActive"
                              : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="downsampleMode"
                            value="cap_both"
                            checked={
                              !disableChartDownsampleDraft &&
                              !disableMapDownsampleDraft
                            }
                            onChange={() => {
                              setDisableChartDownsampleDraft(false);
                              setDisableMapDownsampleDraft(false);
                            }}
                          />
                          両方間引く
                        </label>
                        <label
                          className={`bpslogSettingsRadio ${
                            disableChartDownsampleDraft &&
                            !disableMapDownsampleDraft
                              ? "isActive"
                              : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="downsampleMode"
                            value="no_cap_chart"
                            checked={
                              disableChartDownsampleDraft &&
                              !disableMapDownsampleDraft
                            }
                            onChange={() => {
                              setDisableChartDownsampleDraft(true);
                              setDisableMapDownsampleDraft(false);
                            }}
                          />
                          グラフだけ間引かない
                        </label>
                        <label
                          className={`bpslogSettingsRadio ${
                            !disableChartDownsampleDraft &&
                            disableMapDownsampleDraft
                              ? "isActive"
                              : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="downsampleMode"
                            value="no_cap_map"
                            checked={
                              !disableChartDownsampleDraft &&
                              disableMapDownsampleDraft
                            }
                            onChange={() => {
                              setDisableChartDownsampleDraft(false);
                              setDisableMapDownsampleDraft(true);
                            }}
                          />
                          地図だけ間引かない
                        </label>
                        <label
                          className={`bpslogSettingsRadio ${
                            disableChartDownsampleDraft &&
                            disableMapDownsampleDraft
                              ? "isActive"
                              : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="downsampleMode"
                            value="no_cap_both"
                            checked={
                              disableChartDownsampleDraft &&
                              disableMapDownsampleDraft
                            }
                            onChange={() => {
                              setDisableChartDownsampleDraft(true);
                              setDisableMapDownsampleDraft(true);
                            }}
                          />
                          両方間引かない
                        </label>
                      </>
                    )}
                  </div>
                </label>
              </div>
            </>
          ) : null}
          {!showHeatmapCellMeters ? (
            <div className="bpslogSettingsSection" aria-label="グラフ設定">
              <div className="bpslogSettingsSectionTitle">グラフ</div>
              <div className="bpslogSettingsSectionBody">
                <div className="bpslogSettingsCharts">
                  {chartsDraft.map((c, idx) => (
                    <div key={idx} className="bpslogSettingsChartRow">
                      <label className="bpslogSettingsSeries">
                        <span>系列</span>
                        <select
                          value={c.key}
                          onChange={(e) =>
                            handleChangeSeries(idx, e.target.value as ChartKey)
                          }
                          aria-label={`chart-${idx + 1} series`}
                        >
                          <option value="l1">L1</option>
                          <option value="l2">L2</option>
                          <option value="l3">L3</option>
                          <option value="l4">L4</option>
                          <option value="actual">Actual</option>
                        </select>
                      </label>

                      <label className="bpslogSettingsName">
                        <span>名称</span>
                        <input
                          type="text"
                          value={c.name}
                          onChange={(e) =>
                            updateChart(idx, { name: e.target.value })
                          }
                          aria-label={`${c.key} name`}
                        />
                      </label>

                      <label className="bpslogSettingsColor">
                        <span>色</span>
                        <input
                          type="color"
                          value={c.color}
                          onChange={(e) =>
                            updateChart(idx, { color: e.target.value })
                          }
                          aria-label={`${c.key} color`}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
          <div className="bpslogSettingsSection" aria-label="マップ設定">
            <div className="bpslogSettingsSectionTitle">マップ</div>
            <div className="bpslogSettingsSectionBody">
              <label className="bpslogModalField">
                <span>表示モード</span>
                <div
                  className="bpslogSettingsRadioGroup"
                  role="radiogroup"
                  aria-label="Map tile layer"
                >
                  <label
                    className={`bpslogSettingsRadio ${
                      mapTileLayerDraft === "dark" ? "isActive" : ""
                    }`}
                  >
                    <label
                      className={`bpslogSettingsRadio ${
                        mapTileLayerDraft === "osm" ? "isActive" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="mapTileLayer"
                        value="osm"
                        checked={mapTileLayerDraft === "osm"}
                        onChange={() => setMapTileLayerDraft("osm")}
                      />
                      Light
                    </label>
                    <input
                      type="radio"
                      name="mapTileLayer"
                      value="dark"
                      checked={mapTileLayerDraft === "dark"}
                      onChange={() => setMapTileLayerDraft("dark")}
                    />
                    Dark
                  </label>
                </div>
              </label>

              {!showHeatmapCellMeters ? (
                <label className="bpslogModalField">
                  <span>追従</span>
                  <div className="bpslogSettingsFollowField">
                    <input
                      type="checkbox"
                      checked={followMapDraft}
                      onChange={(e) => setFollowMapDraft(e.target.checked)}
                      aria-label="Map follow"
                    />
                    <span>追従する</span>
                  </div>
                </label>
              ) : null}

              <label className="bpslogModalField">
                <span>閾値</span>
                <div className="bpslogSettingsActualThresholds">
                  <label className="bpslogSettingsThresholdItem">
                    <span>低</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      step={1}
                      min={0}
                      value={thresholdLowKbpsInput}
                      onChange={(e) => setThresholdLowKbpsInput(e.target.value)}
                      aria-label="Actual bitrate low threshold kbps"
                    />
                    <span>kbps</span>
                  </label>
                  <label className="bpslogSettingsThresholdItem">
                    <span>中</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      step={1}
                      min={0}
                      value={thresholdMidKbpsInput}
                      onChange={(e) => setThresholdMidKbpsInput(e.target.value)}
                      aria-label="Actual bitrate mid threshold kbps"
                    />
                    <span>kbps</span>
                  </label>
                </div>
              </label>
              {!showHeatmapCellMeters ? (
                <label className="bpslogModalField">
                  <span>表示色</span>
                  <div className="bpslogSettingsActualColors">
                    <label className="bpslogSettingsActualColorItem">
                      <span>低</span>
                      <input
                        type="color"
                        value={lowColorDraft}
                        onChange={(e) => setLowColorDraft(e.target.value)}
                        aria-label="Actual bitrate low color"
                      />
                    </label>
                    <label className="bpslogSettingsActualColorItem">
                      <span>中</span>
                      <input
                        type="color"
                        value={midColorDraft}
                        onChange={(e) => setMidColorDraft(e.target.value)}
                        aria-label="Actual bitrate mid color"
                      />
                    </label>
                    <label className="bpslogSettingsActualColorItem">
                      <span>高</span>
                      <input
                        type="color"
                        value={highColorDraft}
                        onChange={(e) => setHighColorDraft(e.target.value)}
                        aria-label="Actual bitrate high color"
                      />
                    </label>
                  </div>
                </label>
              ) : null}
            </div>
          </div>
          {showHeatmapCellMeters ? (
            <div
              className="bpslogSettingsSection"
              aria-label="ヒートマップ設定"
            >
              <div className="bpslogSettingsSectionTitle">ヒートマップ</div>
              <div className="bpslogSettingsSectionBody">
                <label className="bpslogModalField">
                  <span>表示方式</span>
                  <div
                    className="bpslogSettingsRadioGroup"
                    role="radiogroup"
                    aria-label="Heatmap render mode"
                  >
                    <label
                      className={`bpslogSettingsRadio ${
                        heatmapRenderModeDraft === "smooth" ? "isActive" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="heatmapRenderMode"
                        value="smooth"
                        checked={heatmapRenderModeDraft === "smooth"}
                        onChange={() => setHeatmapRenderModeDraft("smooth")}
                      />
                      Smooth
                    </label>
                    <label
                      className={`bpslogSettingsRadio ${
                        heatmapRenderModeDraft === "grid" ? "isActive" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="heatmapRenderMode"
                        value="grid"
                        checked={heatmapRenderModeDraft === "grid"}
                        onChange={() => setHeatmapRenderModeDraft("grid")}
                      />
                      Grid
                    </label>
                  </div>
                </label>
                <label className="bpslogModalField">
                  <span>セルサイズ</span>
                  <div className="bpslogSettingsCellSizeField">
                    <input
                      type="number"
                      inputMode="numeric"
                      step={1}
                      min={1}
                      max={5000}
                      value={heatmapCellMetersInput}
                      onChange={(e) =>
                        setHeatmapCellMetersInput(e.target.value)
                      }
                      aria-label="Heatmap cell meters"
                    />
                    <span className="bpslogSettingsCellSizeUnit">m</span>
                  </div>
                </label>
                <label className="bpslogModalField">
                  <span>基本透明度</span>
                  <div className="bpslogSettingsOpacityField">
                    <input
                      type="number"
                      inputMode="numeric"
                      step={1}
                      min={0}
                      max={100}
                      value={heatmapBaseOpacityPctInput}
                      onChange={(e) =>
                        setHeatmapBaseOpacityPctInput(e.target.value)
                      }
                      aria-label="Heatmap base opacity percent"
                    />
                    <span className="bpslogSettingsOpacityUnit">%</span>
                  </div>
                </label>
                <label className="bpslogModalField">
                  <span>スコア閾値</span>
                  <div className="bpslogSettingsScoreThresholds">
                    <label className="bpslogSettingsThresholdItem">
                      <span>黄</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        step={0.05}
                        min={0}
                        max={1}
                        value={heatmapGridScoreColorMidInput}
                        onChange={(e) =>
                          setHeatmapGridScoreColorMidInput(e.target.value)
                        }
                        aria-label="Heatmap score mid color stop"
                      />
                    </label>
                    <label className="bpslogSettingsThresholdItem">
                      <span>赤</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        step={0.05}
                        min={0}
                        max={1}
                        value={heatmapGridScoreColorLowInput}
                        onChange={(e) =>
                          setHeatmapGridScoreColorLowInput(e.target.value)
                        }
                        aria-label="Heatmap score low color stop"
                      />
                    </label>
                  </div>
                </label>
              </div>
            </div>
          ) : null}
          {!showHeatmapCellMeters ? (
            <div className="bpslogSettingsSection" aria-label="その他設定">
              <div className="bpslogSettingsSectionTitle">その他</div>
              <div className="bpslogSettingsSectionBody">
                <label className="bpslogModalField">
                  <span>映像表示</span>
                  <div className="bpslogSettingsFollowField">
                    <input
                      type="checkbox"
                      checked={showVideoDraft}
                      onChange={(e) => setShowVideoDraft(e.target.checked)}
                      aria-label="Video visibility"
                    />
                    <span>表示する</span>
                  </div>
                </label>

                <label className="bpslogModalField">
                  <span>Delay</span>
                  <div className="bpslogSettingsDelayField">
                    <input
                      type="number"
                      value={delayOffsetInput}
                      onChange={(e) => setDelayOffsetInput(e.target.value)}
                      step={1}
                      inputMode="numeric"
                      aria-label="Delay offset ms"
                    />
                    <span className="bpslogSettingsDelayUnit">ms</span>
                  </div>
                </label>
              </div>
            </div>
          ) : null}
        </div>
        <div className="bpslogSettingsResetRow">
          <button type="button" className="defaultButton" onClick={handleReset}>
            デフォルトにリセット
          </button>
        </div>
        <div className="bpslogModalFooter">
          <button type="button" className="bpslogButton" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bpslogButton bpslogButton--primary"
            onClick={handleApply}
            disabled={!canApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SettingsModal;
